import * as React from 'react';

import { useForm } from 'react-hook-form';

import { requestResetPassword } from 'services/srp-auth';
import useStatus from 'hooks/use-status';
import { useTurnstileValidation } from 'hooks/use-turnstile-validation';
import { FormValues } from './types';

import Button from 'components/button';
import Input from 'components/inputs/floating';
import FormErrors from 'components/form-errors';
import Logo from 'components/logo';
import StatusText from 'components/status-text';
import { Heading, Text } from 'components/text';
import Turnstile from 'components/turnstile';

import * as Styles from './styles';
import useClientId from 'hooks/use-client-id';

const PasswordResetForm: React.FC = () => {
  const { status, Transition } = useStatus();
  const [apiError, setApiError] = React.useState('');
  const [turnstileToken, setTurnstileToken] = React.useState<string | null>(
    null,
  );
  const { validateToken } = useTurnstileValidation();
  const { register, handleSubmit, errors, getValues } = useForm<FormValues>();

  const [emailSent, setEmailSent] = React.useState(false);

  const client = useClientId();
  const isTcDeluxe = client.includes('deluxe');

  const onSubmit = async (data: FormValues) => {
    if (!turnstileToken) {
      setApiError('Please complete the verification');
      return;
    }

    setApiError('');
    Transition.loading();

    const isValid = await validateToken(turnstileToken);
    if (!isValid) {
      setApiError('Verification failed. Please try again.');
      Transition.error();
      return;
    }

    requestResetPassword(data.email)
      .then((res) => {
        if (res.status === 'error') {
          setApiError(res.msg);
          Transition.error();
        } else {
          setEmailSent(true);
          Transition.success(2000);
        }
      })
      .catch(() => {
        setApiError('Failed to request password reset. Please try again.');
        Transition.error();
      });
  };

  return (
    <Styles.Wrapper style={{ background: isTcDeluxe ? '#282828' : 'auto' }}>
      <Logo mb={4} />
      <Heading fontSize={3} mb={3} color={isTcDeluxe ? '#eac554' : 'auto'}>
        Enter your email address to reset your password
      </Heading>
      {apiError && (
        <Text color="error.main" fontSize={1} mb={3}>
          {apiError}
        </Text>
      )}
      <FormErrors errors={errors} />
      <Styles.Form onSubmit={handleSubmit(onSubmit)}>
        <Input
          label="Email"
          ref={register({ required: true })}
          inputProps={{
            name: 'email',
            type: 'email',
          }}
        />

        <Turnstile
          onSuccess={(token) => {
            setTurnstileToken(token);
            setApiError('');
          }}
          onError={() => {
            setTurnstileToken(null);
            setApiError('Verification failed. Please try again.');
          }}
          onExpired={() => {
            setTurnstileToken(null);
            setApiError('Verification expired. Please complete it again.');
          }}
          theme="auto"
          size="normal"
        />

        <Button
          kind="filled"
          variant="primary"
          type="submit"
          py={3}
          mt={2}
          disabled={status === 'loading' || !turnstileToken}
        >
          <StatusText message="Reset password" status={status} fontSize={2} />
        </Button>
        {emailSent ? (
          <Text fontSize={1} mb={3} color={isTcDeluxe ? '#eac554' : 'auto'}>
            If there is an account associated with {getValues().email}, an email
            with password reset instructions will be sent to it.
          </Text>
        ) : null}
      </Styles.Form>
    </Styles.Wrapper>
  );
};

export default PasswordResetForm;
